import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, TextField, Button, Typography, Paper, CircularProgress, Alert, Fade, Grow, Zoom } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SchoolIcon from '@mui/icons-material/School';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import BlueCBackground from '../../assets/images/BlueC.jpg';

// Styled components for animations
const AnimatedContainer = motion(Container);
const AnimatedPaper = motion(Paper);
const AnimatedBox = motion(Box);

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [setupMode, setSetupMode] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [messageType, setMessageType] = useState('info'); // 'error', 'success', 'info'
  const [appearAnimation, setAppearAnimation] = useState(false);
  
  const { login, setupAdmin, error, setError, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // Animation variants for staggered animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    } else {
      // Trigger appear animation after a short delay
      setTimeout(() => {
        setAppearAnimation(true);
      }, 100);
    }
    
    // Check if admin setup is needed
    const checkSetupNeeded = async () => {
      try {
        // We'll make a request to the login endpoint. If we get a 404 or a specific error
        // message about admin setup being needed, we'll switch to setup mode
        setLoading(true);
        await fetch('/api/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: 'check_setup', password: 'check_setup' })
        });
        
        // If we didn't get redirected to setup, it means login exists
        setSetupMode(false);
      } catch (error) {
        // Check if the error indicates setup is needed
        if (error.response && error.response.status === 404) {
          setSetupMode(true);
          setMessageType('info');
          setStatusMessage('Initial setup required. Please create an admin account.');
        }
      } finally {
        setLoading(false);
      }
    };
    
    checkSetupNeeded();
  }, [isAuthenticated, navigate]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      setMessageType('error');
      setStatusMessage('Username and password are required');
      return;
    }
    
    setLoading(true);
    setStatusMessage('');
    
    try {
      if (setupMode) {
        // Handle admin setup
        const result = await setupAdmin(username, password);
        
        if (result.success) {
          setMessageType('success');
          setStatusMessage('Admin setup completed. You can now log in.');
          setSetupMode(false);
        } else {
          setMessageType('error');
          setStatusMessage(result.message || 'Setup failed');
        }
      } else {
        // Handle regular login
        const success = await login(username, password);
        
        if (success) {
          navigate('/');
        } else {
          setMessageType('error');
          setStatusMessage(error || 'Login failed');
        }
      }
    } catch (error) {
      setMessageType('error');
      setStatusMessage('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `url(${BlueCBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        padding: 2
      }}
    >
      <AnimatedContainer 
        component="main" 
        maxWidth="sm"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <Fade in={appearAnimation} timeout={800}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              height: { sm: '500px' },
              boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
              borderRadius: 4,
              overflow: 'hidden',
              backgroundColor: 'rgba(255, 255, 255, 0.95)'
            }}
          >
          {/* Left side - College branding */}
          <Box 
            sx={{
              flex: '1',
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: 'primary.main',
              color: 'white',
              p: 4,
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <AnimatedBox
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              sx={{ position: 'relative', zIndex: 10 }}
            >
              <Zoom in={appearAnimation} timeout={1000}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                  <CalendarMonthIcon sx={{ fontSize: 80 }} />
                </Box>
              </Zoom>
              
              <Typography variant="h4" fontWeight="bold" mb={2}>
                College Timetable
              </Typography>
              
              <Typography variant="body1" opacity={0.9}>
                Manage your college schedule efficiently and effectively.
              </Typography>
              
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
                {[1, 2, 3].map((_, i) => (
                  <Box 
                    key={i}
                    component={motion.div}
                    animate={{ 
                      y: [0, -10, 0], 
                      opacity: [0.7, 1, 0.7],
                    }}
                    transition={{ 
                      duration: 2, 
                      repeat: Infinity, 
                      delay: i * 0.4,
                      ease: "easeInOut" 
                    }}
                    sx={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: '50%', 
                      bgcolor: 'white',
                    }}
                  />
                ))}
              </Box>
            </AnimatedBox>
            
            {/* Floating background elements */}
            {[1, 2, 3, 4, 5].map((_, i) => (
              <Box
                key={i}
                component={motion.div}
                animate={{
                  x: [0, 10, 0],
                  y: [0, 15, 0],
                  opacity: [0.1, 0.2, 0.1],
                }}
                transition={{
                  duration: Math.random() * 5 + 5,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
                sx={{
                  position: 'absolute',
                  width: Math.random() * 60 + 40,
                  height: Math.random() * 60 + 40,
                  borderRadius: '50%',
                  bgcolor: 'white',
                  opacity: 0.1,
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                }}
              />
            ))}
          </Box>
          
          {/* Right side - Login form */}
          <AnimatedPaper
            variants={itemVariants}
            sx={{
              flex: '1',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
              bgcolor: 'background.paper',
              borderRadius: { xs: 4, sm: '0 4px 4px 0' },
            }}
          >
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 260, damping: 20 }}>
              <Box 
                sx={{ 
                  bgcolor: 'primary.main', 
                  color: 'white', 
                  p: 2, 
                  borderRadius: '50%',
                  mb: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: 56,
                  height: 56,
                }}
              >
                <LockOutlinedIcon />
              </Box>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <Typography component="h1" variant="h5" mb={3} fontWeight="bold" textAlign="center">
                {setupMode ? 'Admin Setup' : 'Login to Dashboard'}
              </Typography>
            </motion.div>
            
            {statusMessage && (
              <Grow in={!!statusMessage}>
                <Alert 
                  severity={messageType} 
                  sx={{ mb: 2, width: '100%' }} 
                  onClose={() => setStatusMessage('')}
                >
                  {statusMessage}
                </Alert>
              </Grow>
            )}
            
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
              <motion.div variants={itemVariants}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Username"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                  variant="outlined"
                  InputProps={{
                    sx: { borderRadius: 2 }
                  }}
                />
              </motion.div>
              
              <motion.div variants={itemVariants}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete={setupMode ? 'new-password' : 'current-password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  variant="outlined"
                  InputProps={{
                    sx: { borderRadius: 2 }
                  }}
                />
              </motion.div>
              
              <motion.div variants={itemVariants}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ 
                    mt: 3, 
                    mb: 2, 
                    py: 1.5, 
                    borderRadius: 2,
                    boxShadow: '0 4px 14px 0 rgba(25, 118, 210, 0.39)',
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                  disabled={loading}
                  component={motion.button}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : setupMode ? (
                    'Create Admin Account'
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </motion.div>
            </Box>
          </AnimatedPaper>
          </Box>
        </Fade>
      </AnimatedContainer>
    </Box>
  );
};

export default Login;
